import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';
import { Prayer } from '../../models/prayer.model';
import * as prayerActions from '../actions/prayer.actions';

export interface PrayerState extends EntityState<Prayer> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  prayerStatsLoading: boolean;
  error: AppExceptionModel | undefined;
}

export const prayerAdapter: EntityAdapter<Prayer> =
  createEntityAdapter<Prayer>();

export const initialState: PrayerState = prayerAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  prayerStatsLoading: false,
  error: undefined,
});

export const prayerReducer = createReducer(
  initialState,
  on(prayerActions.getPrayers, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(prayerActions.getPrayer, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(prayerActions.loadPrayers, (state, { prayers }) => {
    if (prayers?.length > 0) {
      return prayerAdapter.upsertMany(prayers, {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      });
    } else {
      return {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      };
    }
  }),
  on(prayerActions.loadPrayer, (state, { prayer }) => {
    return prayerAdapter.upsertOne(prayer, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    });
  }),
  on(prayerActions.getPrayersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(prayerActions.create, (state, { prayer }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(prayerActions.createSuccess, (state, { prayerId }) => ({
    ...state,
    error: undefined,
    inProgress: false,
  })),
  on(prayerActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(prayerActions.update, (state, { prayer }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(prayerActions.updateSuccess, (state, { prayer }) =>
    prayerAdapter.addOne(prayer, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(prayerActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(prayerActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(prayerActions.deleteSuccess, (state, { id }) =>
    prayerAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(prayerActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(prayerActions.getPrayerStats, (state) => ({
    ...state,
    prayerStatsLoading: true,
  })),
  on(prayerActions.getPrayerStatsDone, (state) => ({
    ...state,
    prayerStatsLoading: false,
  })),
  on(prayerActions.getPrayerStatsFailure, (state) => ({
    ...state,
    prayerStatsLoading: false,
  })),
  on(prayerActions.updateStatus, (state, { prayer }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(prayerActions.updateStatusSuccess, (state, { prayer }) =>
    prayerAdapter.upsertOne(prayer, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(prayerActions.updateStatusFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(prayerActions.clear, (state) => initialState),
);
